/* eslint-disable react-hooks/exhaustive-deps */
import { useCallback, useEffect, useState } from "react";
import { useFormik } from "formik";
import * as yup from "yup";
import {
  Box,
  Button,
  Container,
  FormControl,
  FormControlLabel,
  FormHelperText,
  FormLabel,
  Grid,
  InputLabel,
  MenuItem,
  Radio,
  RadioGroup,
  Select,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import dayjs from "dayjs";
import "dayjs/locale/es";
import { ProgressBar } from "@components/ProgressBar";
import CustomRightImage from "@components/CustomRightImagev2";
import { InactivityHandler } from "@components/InactivityHandler";
import { useLazyGetStatesQuery } from "@api/catalogs";
import { CurpQuery, StateStructure } from "@interfaces/index";
import { CustomSwitch } from "@components/CustomSwitch";
import { CURP, RFC_HOMOCLAVE, RFC_PFAE_SIN_CLAVE } from "@utils/expreciones";
import useStatusConfig from "@hooks/useStatusConfig";
import useTofuSteps from "@hooks/useTofuSteps";
import { useNavigate } from "react-router-dom";
import { usePostValidateNextStageMutation } from "@api/applications";
import { RootState, useAppDispatch } from "@store/store";
import { useSelector } from "react-redux";
import { useLazyGetCurpQuery } from "@api/onboarding";
import { convertSex, extractRFCFromCurp } from "@helpers/funciones";
import { setClientDetailsData, setLegalRepresentative } from "@store/slices/registerSlice";
import { usePatchCuentaMutation } from "@api/account";
import * as Sentry from "@sentry/react";
import { ApiError } from "@interfaces/response";
import { setModalErrorVisible } from "@store/slices/appSlice";
import { usePatchPersonMutation } from "@api/personasApi";

const MAX_DATE = dayjs().subtract(18, "years");

const SCREEN_STATUS = "CSDOF";

const InformacionPlataforma = () => {
  // hooks
  const theme = useTheme();
  const isMobileOrTablet = useMediaQuery(theme.breakpoints.down("md"));
  const { numberOfSteps, currentStep } = useTofuSteps(SCREEN_STATUS);
  const { nextStatus } = useStatusConfig(SCREEN_STATUS);
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  // redux hooks
  const application = useSelector((state: RootState) => state.register.application);
  const accountUser = useSelector((state: RootState) => state.register.account_user);
  const clientDetails = useSelector((state: RootState) => state.register.personal_data);
  const legalRepresentative = useSelector((state: RootState) => state.register.legal_representative);

  // api hooks
  const [getStatesTrigger] = useLazyGetStatesQuery();
  const [getCurp] = useLazyGetCurpQuery();
  const [triggerPatchCuenta] = usePatchCuentaMutation();
  const [triggerPostNextStage] = usePostValidateNextStageMutation();
  const [patchPerson] = usePatchPersonMutation();

  // state
  const [isLoadingCatalogs, setIsLoadingCatalogs] = useState(false);
  const [placesCatalog, setPlacesCatalog] = useState<StateStructure[]>([]);
  const [isFetchingCurp, setIsFetchingCurp] = useState(false);

  const formik = useFormik({
    initialValues: {
      birth_date: "",
      birth_state: "",
      sex: "",
      rfc: "",
      homoclave: "",
      curp: "",
      confirmDeclaration: false,
    },
    validationSchema: yup.object().shape({
      birth_date: yup
        .string()
        .required("Campo requerido")
        .test("is-valid-date", "Fecha inválida", (value) => {
          if (!value) return false;
          return dayjs(value).isValid();
        }),
      birth_state: yup.string().required("Campo requerido"),
      sex: yup.string().oneOf(["MASCULINO", "FEMENINO"], "Debe seleccionar un género").required("Campo requerido"),
      rfc: yup.string().required("Campo requerido").matches(RFC_PFAE_SIN_CLAVE, "Formato de RFC no válido"),
      homoclave: yup.string().required("Campo requerido").matches(RFC_HOMOCLAVE, "Formato de homoclave no válido"),
      curp: yup.string().required("Campo requerido").matches(CURP, "Formato de CURP no válido"),
      confirmDeclaration: yup.boolean().oneOf([true], "Debes confirmar para continuar"),
    }),
    onSubmit: async (values, { setSubmitting }) => {
      try {
        if (!nextStatus) {
          throw new Error("No se encontró el siguiente status");
        }

        if (!legalRepresentative?.id) {
          throw new Error("No se encontró el representante legal");
        }

        setSubmitting(true);

        const sex = values.sex === "MASCULINO" ? "MASCULINO" : "FEMENINO";
        const birth_date = dayjs(values.birth_date).format("YYYY-MM-DD");

        // guardar los datos en cliente details
        await triggerPatchCuenta({
          accountId: clientDetails.id,
          body: {
            company_rfc: values.rfc + values.homoclave,
            curp: values.curp,
            birth_state: Number(values.birth_state),
            birth_date,
            sex,
          },
        }).unwrap();

        // actualizar la persona
        const responsePatchPerson = await patchPerson({
          personId: legalRepresentative.id,
          body: {
            birth_date,
            birth_state: Number(values.birth_state),
            sex,
            rfc: values.rfc + values.homoclave,
            curp: values.curp,
          },
        }).unwrap();

        // actualizar el representante legal en el estado de redux
        dispatch(setLegalRepresentative(responsePatchPerson.data));

        // guardar los datos en el estado de redux
        dispatch(
          setClientDetailsData({
            rfc: values.rfc + values.homoclave,
            curp: values.curp,
            birth_state: values.birth_state,
            birth_date,
            sex,
          }),
        );

        // mover al siguiente status
        await triggerPostNextStage({
          applicationId: application.id,
          statusCode: nextStatus?.code,
          username: `TOFU - ${accountUser.email}`,
        }).unwrap();

        // mover a la siguiente pantalla
        navigate(nextStatus?.url);
      } catch (error) {
        const api_error = error as ApiError;
        Sentry.captureException(error, {
          extra: {
            component: "DatosOficiales-onSubmit",
            values: formik.values,
          },
        });
        dispatch(setModalErrorVisible({ open: true, error: api_error }));
      } finally {
        setSubmitting(false);
      }
    },
  });

  const fetchCurp = useCallback(async () => {
    try {
      if (!legalRepresentative) {
        return;
      }
      const { birth_date, birth_state, sex } = formik.values;
      if (!birth_date || !birth_state || !sex) {
        return;
      }

      setIsFetchingCurp(true);
      const birthStateNumber = Number(birth_state);
      const birthStateFormatted = birthStateNumber.toString().padStart(2, "0");
      const payloadGetCurp: CurpQuery = {
        nombres: legalRepresentative.name,
        apellido_paterno: legalRepresentative.last_name,
        apellido_materno: legalRepresentative.last_name_2,
        estado_nacimiento: birthStateFormatted,
        fecha_nacimiento: dayjs(birth_date, "YYYY-MM-DD").format("DD-MM-YYYY"),
        sexo: convertSex(sex),
      };
      const responseGetCurp = await getCurp(payloadGetCurp).unwrap();
      const rfc = extractRFCFromCurp(responseGetCurp.data.curp);
      formik.setFieldValue("curp", responseGetCurp.data.curp);
      formik.setFieldValue("rfc", rfc);
      formik.setFieldValue("homoclave", "");

      setTimeout(() => {
        formik.validateForm();
      }, 200);
    } catch (error) {
      console.error("Hubo un error al obtener la CURP: ", error);
    } finally {
      setIsFetchingCurp(false);
    }
  }, [formik.values.birth_date, formik.values.birth_state, formik.values.sex]);

  useEffect(() => {
    fetchCurp();
  }, [formik.values.birth_date, formik.values.birth_state, formik.values.sex]);

  const fetchInitialData = useCallback(async () => {
    try {
      setIsLoadingCatalogs(true);

      const [responseStates] = await Promise.all([getStatesTrigger().unwrap()]);
      setPlacesCatalog(responseStates);

      if (legalRepresentative) {
        formik.setFieldValue("birth_date", legalRepresentative.birth_date ? legalRepresentative.birth_date : "");
        formik.setFieldValue("birth_state", legalRepresentative.birth_state || "");
        formik.setFieldValue("sex", legalRepresentative.sex || "");
      }
    } catch (error) {
      const apiError = error as ApiError;
      Sentry.captureException(error, {
        extra: {
          component: "DatosOficiales-fetchInitialData",
          values: formik.values,
        },
      });
      dispatch(setModalErrorVisible({ open: true, error: apiError }));
    } finally {
      setIsLoadingCatalogs(false);
    }
  }, [getStatesTrigger]);

  useEffect(() => {
    fetchInitialData();
  }, []);

  return (
    <Grid container height="100%">
      <Grid item xs={12} md={8} mt={2}>
        <InactivityHandler />
        <Box
          sx={{
            mt: 2,
            mx: 4,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
          }}
        >
          <Typography
            variant={isMobileOrTablet ? "h4" : "h3"}
            fontWeight={500}
            textAlign="center"
            color={"primary.dark"}
          >
            Datos oficiales
          </Typography>

          <Container maxWidth="sm" sx={{ mt: isMobileOrTablet ? 2 : 3 }}>
            <ProgressBar totalSteps={numberOfSteps} currentStep={currentStep} />
          </Container>

          <Container maxWidth="sm" sx={{ mt: isMobileOrTablet ? 2 : 3 }}>
            <form onSubmit={formik.handleSubmit} noValidate>
              <Typography
                variant={isMobileOrTablet ? "body2" : "body1"}
                fontWeight={500}
                textAlign="center"
                sx={{ whiteSpace: "pre-line" }}
                color={"primary.dark"}
              >
                Ingresa los siguientes datos para el calculo de{"\n"}CURP
              </Typography>

              <FormControl fullWidth sx={{ mt: 3 }}>
                <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="es">
                  <DatePicker
                    label="Fecha de nacimiento"
                    value={formik.values.birth_date ? dayjs(formik.values.birth_date) : null}
                    onChange={(value) => {
                      const formattedDate = value ? dayjs(value).format("YYYY-MM-DD") : "";
                      formik.setFieldValue("birth_date", formattedDate);
                    }}
                    maxDate={MAX_DATE}
                    slotProps={{
                      textField: {
                        name: "birth_date",
                        onBlur: formik.handleBlur,
                        error: formik.touched.birth_date && Boolean(formik.errors.birth_date),
                        helperText: formik.touched.birth_date ? formik.errors.birth_date : "",
                        required: true,
                        placeholder: "DD / MM / AAAA",
                      },
                    }}
                    disabled={formik.isSubmitting}
                  />
                </LocalizationProvider>
              </FormControl>

              <FormControl
                fullWidth
                required
                sx={{ mt: 2 }}
                error={formik.touched.birth_state && Boolean(formik.errors.birth_state)}
              >
                <InputLabel id="birth_state-label">Lugar de nacimento</InputLabel>
                <Select
                  labelId="birth_state-label"
                  id="birth_state"
                  name="birth_state"
                  fullWidth
                  required
                  label="Lugar de nacimento"
                  value={formik.values.birth_state}
                  onBlur={formik.handleBlur}
                  onChange={formik.handleChange}
                  error={formik.touched.birth_state && Boolean(formik.errors.birth_state)}
                  disabled={formik.isSubmitting || isLoadingCatalogs}
                >
                  <MenuItem value={""}>Selecciona</MenuItem>
                  {placesCatalog.map((item: StateStructure) => (
                    <MenuItem key={item.id} value={item.id}>
                      {item.name}
                    </MenuItem>
                  ))}
                </Select>
                {Boolean(formik.touched.birth_state) && Boolean(formik.errors.birth_state) && (
                  <FormHelperText>{formik.errors.birth_state}</FormHelperText>
                )}
              </FormControl>

              <FormControl
                component="fieldset"
                sx={{ mt: 1 }}
                error={formik.touched.sex && formik.errors.sex ? true : false}
              >
                <FormLabel component="legend">Sexo como aparece en tu INE</FormLabel>
                <RadioGroup aria-label="sex" name="sex" value={formik.values.sex} onChange={formik.handleChange} row>
                  <FormControlLabel
                    sx={{ color: "primary.dark" }}
                    value="MASCULINO"
                    control={<Radio />}
                    label="Masculino"
                    disabled={formik.isSubmitting}
                  />
                  <FormControlLabel
                    sx={{ color: "primary.dark" }}
                    value="FEMENINO"
                    control={<Radio />}
                    label="Femenino"
                    disabled={formik.isSubmitting}
                  />
                </RadioGroup>
                {formik.touched.sex && formik.errors.sex ? <FormHelperText>Selecciona tu sexo</FormHelperText> : null}
              </FormControl>

              <Typography
                variant={isMobileOrTablet ? "body2" : "body1"}
                fontWeight={500}
                textAlign="center"
                color={"primary.dark"}
                sx={{ mt: 2 }}
              >
                Confirma tu RFC y CURP
              </Typography>

              <Grid container columnSpacing={2} sx={{ mt: 1 }}>
                <Grid item xs={8}>
                  <TextField
                    id="rfc"
                    name="rfc"
                    label="RFC accionista"
                    margin="normal"
                    required
                    fullWidth
                    autoComplete="off"
                    onChange={(e) => {
                      const input = e.target;
                      const value = e.target.value.toUpperCase();
                      const start = input.selectionStart;
                      formik.handleChange(e);
                      formik.setFieldValue("rfc", value);
                      setTimeout(() => input.setSelectionRange(start, start), 0);
                    }}
                    inputProps={{ maxLength: 10 }}
                    onBlur={formik.handleBlur}
                    value={formik.values.rfc}
                    error={formik.touched.rfc && Boolean(formik.errors.rfc)}
                    helperText={formik.touched.rfc && formik.errors.rfc}
                    disabled={formik.isSubmitting || isFetchingCurp}
                  />
                </Grid>

                <Grid item xs={4}>
                  <TextField
                    id="homoclave"
                    name="homoclave"
                    label="Homoclave"
                    margin="normal"
                    required
                    fullWidth
                    autoComplete="off"
                    onChange={(e) => {
                      const input = e.target;
                      const value = e.target.value.toUpperCase();
                      const start = input.selectionStart;
                      formik.handleChange(e);
                      formik.setFieldValue("homoclave", value);
                      setTimeout(() => input.setSelectionRange(start, start), 0);
                    }}
                    onBlur={formik.handleBlur}
                    value={formik.values.homoclave}
                    error={formik.touched.homoclave && Boolean(formik.errors.homoclave)}
                    helperText={formik.touched.homoclave && formik.errors.homoclave}
                    inputProps={{ maxLength: 3 }}
                    disabled={formik.isSubmitting || isFetchingCurp}
                  />
                </Grid>

                <Grid item xs={12}>
                  <TextField
                    id="curp"
                    name="curp"
                    label="CURP"
                    margin="normal"
                    required
                    fullWidth
                    autoComplete="off"
                    onChange={(e) => {
                      const input = e.target;
                      const value = e.target.value.toUpperCase();
                      const start = input.selectionStart;
                      formik.handleChange(e);
                      formik.setFieldValue("curp", value);
                      setTimeout(() => input.setSelectionRange(start, start), 0);
                    }}
                    onBlur={formik.handleBlur}
                    value={formik.values.curp}
                    error={formik.touched.curp && Boolean(formik.errors.curp)}
                    helperText={formik.touched.curp && formik.errors.curp}
                    inputProps={{ maxLength: 18 }}
                    disabled={formik.isSubmitting || isFetchingCurp}
                  />
                </Grid>

                <Grid item xs={12} mt={2}>
                  <FormControlLabel
                    control={<CustomSwitch />}
                    label={
                      <Typography variant={"body2"} sx={{ mt: 2, mb: 2 }} color={"primary.dark"}>
                        Declaro bajo protesta de decir verdad que soy el solicitante del crédito.
                      </Typography>
                    }
                    name="confirmDeclaration"
                    checked={formik.values.confirmDeclaration}
                    onChange={formik.handleChange}
                    disabled={formik.isSubmitting}
                  />
                  {formik.touched.confirmDeclaration && Boolean(formik.errors.confirmDeclaration) && (
                    <FormHelperText error>{formik.errors.confirmDeclaration}</FormHelperText>
                  )}
                </Grid>
              </Grid>

              <Box display="flex" justifyContent="center" mt={isMobileOrTablet ? 2 : 3} mb={2}>
                <Button type="submit" variant="default" disabled={!formik.isValid || formik.isSubmitting}>
                  Continuar
                </Button>
              </Box>
            </form>
          </Container>
        </Box>
      </Grid>

      {!isMobileOrTablet && (
        <Grid item xs={12} md={4}>
          <CustomRightImage />
        </Grid>
      )}
    </Grid>
  );
};

export default InformacionPlataforma;
